/** @jsxFrag React.Fragment */
import * as React from 'react';

import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import { getCmsLocaleString } from 'js/lib/language';
import { useLocation, useParams } from 'js/lib/useRouter';

import type { PropsFromWithUserAgent } from 'bundles/page/common/withUserAgent';
import withUserAgent from 'bundles/page/common/withUserAgent';
import type {
  HubPageContentFieldsFragment,
  ProgramHubPageContentQuery,
  ProgramHubPageContentQueryVariables,
} from 'bundles/professional-certificate-category-pages/__generated__/contentfulTypes';
import ExpandedCertificatesContainer from 'bundles/professional-certificate-category-pages/components/certificates/ExpandedCertificatesContainer';
import Container from 'bundles/professional-certificate-category-pages/components/shared/Container';
import Faqs from 'bundles/professional-certificate-category-pages/components/shared/Faqs';
import HubBanner from 'bundles/professional-certificate-category-pages/components/shared/HubBanner';
import InfoCard from 'bundles/professional-certificate-category-pages/components/shared/InfoCard';
import LearnMore from 'bundles/professional-certificate-category-pages/components/shared/LearnMore';
import LearningCommunitySection from 'bundles/professional-certificate-category-pages/components/shared/LearningCommunitySection';
import UnifiedCategoryIntro from 'bundles/professional-certificate-category-pages/components/shared/UnifiedCategoryIntro';
import UnifiedInstructorSpotlight from 'bundles/professional-certificate-category-pages/components/shared/UnifiedInstructorSpotlight';
import UnifiedInstructorsList from 'bundles/professional-certificate-category-pages/components/shared/UnifiedInstructorsList';
import VideoTestimonials from 'bundles/professional-certificate-category-pages/components/shared/VideoTestimonials';
import {
  HubPages,
  ProductType,
  SLUG_TO_CERTIFICATE_CATEGORY,
  VARIANT_ORDER,
} from 'bundles/professional-certificate-category-pages/constants';
import { getCertificatesByCategory } from 'bundles/professional-certificate-category-pages/queries';
import { getPremiumHubPageContent } from 'bundles/professional-certificate-category-pages/queries/contentfulQueries';
import type { CertificateProps } from 'bundles/professional-certificate-category-pages/types';
import { getTitleForVariant } from 'bundles/professional-certificate-category-pages/utils';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';
import HubPageGoogleSchemaMarkup from 'bundles/seo/components/HubPageGoogleSchemaMarkup';

type CertificatesByCategoryData = { CertificatesByCategoryResponse?: { certificateList: Array<CertificateProps> } };

type CertificatesByCategoryVariables = { category: string };

type CertificateCategories = Record<
  string,
  {
    title: string;
    certificates: Array<CertificateProps>;
  }
>;

type PropsFromDataFetcher = {
  pageContent?: HubPageContentFieldsFragment | null;
  certificatesList?: Array<CertificateProps>;
  categorizedCertificates?: CertificateCategories;
};

type Props = PropsFromDataFetcher & PropsFromWithUserAgent;

export const categorizeCertificates = (certificatesList?: Array<CertificateProps>) => {
  if (!certificatesList || certificatesList.length === 0) {
    return {
      certificatesList,
    };
  }

  const categorizedCertificates: CertificateCategories = VARIANT_ORDER.reduce((total, current) => {
    return {
      ...total,
      [current]: {
        title: getTitleForVariant(current),
        certificates: [],
      },
    };
  }, {});

  certificatesList.forEach((certificateInfo: CertificateProps) => {
    if (certificateInfo.productVariant === 'CERTIFICATE_PRODUCT_VARIANT_PROFESSIONAL_CERTIFICATE') {
      categorizedCertificates[certificateInfo.productVariant]?.certificates?.push(certificateInfo);
    }
  });

  return {
    categorizedCertificates,
    certificatesList,
  };
};

export const ProductVariantHubPage = ({ pageContent, certificatesList, categorizedCertificates, userAgent }: Props) => {
  if (!pageContent) {
    return null;
  }

  const {
    slug,
    pageHeading,
    pageSubheading,
    associatedProgramsHeading,
    categoryLinksCollection,
    productVariantCardsCollection,
    learnMoreInfoHeading,
    learnMoreCardsCollection,
    learningCommunityTitle,
    communityStatsCollection,
    faqSectionHeader,
    faqsCollection,
    extraInformation,
    videoTestimonialsHeader,
    primaryVideoTestimonialOverride,
    secondaryVideoTestimonialsOverrideCollection,
    instructorMosaicHeader,
    instructorsCollection,
    instructorSpotlightSectionTitle,
    instructorSpotlightDescription,
    instructorHeadshot,
    seoInformation,
    categoryIntroDescription,
    profilePicture,
    learnerName,
    enrolledProgram,
  } = pageContent;

  return (
    <>
      <main data-test="ProductVariantHubPage">
        <HubPageGoogleSchemaMarkup productList={certificatesList} />
        <CourseraMetatags
          metaNameAndProperties={{
            title: seoInformation?.metaTitle ?? undefined,
            description: seoInformation?.metaDescription ?? undefined,
            disableCourseraSuffix: !seoInformation?.enableCourseraSuffix,
            disableCourseraDescriptionSuffix: !seoInformation?.enableCourseraDescriptionSuffix,
            disableDescLimit: !seoInformation?.enableDescriptionLimit,
          }}
        />
        <HubBanner
          slug={slug}
          pageHeading={pageHeading}
          pageSubheading={pageSubheading}
          currentHubPage={HubPages.ProductVariantHubPage}
          productType={ProductType.Certificates}
          productId={undefined}
          surveyProductType="GenericPage"
        />
        <Container>
          <ExpandedCertificatesContainer
            categorizedCertificates={categorizedCertificates}
            certificatesList={certificatesList}
            title={associatedProgramsHeading}
            categoryLinks={categoryLinksCollection?.items}
            productVariantCards={productVariantCardsCollection?.items}
            userAgent={userAgent}
          />
          <UnifiedCategoryIntro
            categoryIntroDescription={categoryIntroDescription}
            profilePicture={profilePicture}
            learnerName={learnerName}
            enrolledProgram={enrolledProgram}
          />
          <LearnMore
            userAgent={userAgent}
            learnMoreInfoHeading={learnMoreInfoHeading}
            learnMoreCards={learnMoreCardsCollection?.items}
          />
          <VideoTestimonials
            videoTestimonialsHeader={videoTestimonialsHeader}
            primaryVideoTestimonialOverride={primaryVideoTestimonialOverride}
            secondaryVideoTestimonialsOverride={secondaryVideoTestimonialsOverrideCollection?.items}
            productCategories={[slug] as Array<string>}
            productTypes={['certificates']}
          />
          <LearningCommunitySection
            learningCommunityTitle={learningCommunityTitle}
            communityStats={communityStatsCollection?.items}
          />
          <UnifiedInstructorsList
            instructorMosaicHeader={instructorMosaicHeader}
            instructors={instructorsCollection?.items}
          />
          <UnifiedInstructorSpotlight
            sectionTitle={instructorSpotlightSectionTitle}
            profileDescription={instructorSpotlightDescription}
            profileImage={instructorHeadshot}
          />
          <InfoCard userAgent={userAgent} marketingContent={extraInformation} />
        </Container>
        <Faqs faqSectionHeader={faqSectionHeader} faqs={faqsCollection?.items} />
      </main>
    </>
  );
};

const CertificatesCategoryHubPageDataFetcher = () => {
  const params = useParams();
  const location = useLocation();
  const { categorySlug } = params;
  const isPreview = Number(location.query.preview ?? 0) === 1;

  const { data: premiumHubPageContent } = useQuery<ProgramHubPageContentQuery, ProgramHubPageContentQueryVariables>(
    getPremiumHubPageContent,
    {
      variables: {
        slug: categorySlug,
        locale: getCmsLocaleString(),
        preview: isPreview,
        pageType: 'certificates_category_hub',
      },
      context: { clientName: isPreview ? 'contentfulPreviewGql' : 'contentfulGql' },
      errorPolicy: 'all',
    }
  );

  const pageContent = premiumHubPageContent?.programHubPageContentCollection?.items?.[0];

  const { data: certificatesByCategory } = useQuery<CertificatesByCategoryData, CertificatesByCategoryVariables>(
    getCertificatesByCategory,
    {
      variables: {
        category: SLUG_TO_CERTIFICATE_CATEGORY[categorySlug as keyof typeof SLUG_TO_CERTIFICATE_CATEGORY],
      },
      errorPolicy: 'all',
      onError: (error) =>
        Sentry.captureException(error, {
          extra: {
            message: 'Error fetching certificates by category',
          },
        }),
    }
  );

  const uncategorizedCertificatesList = certificatesByCategory?.CertificatesByCategoryResponse?.certificateList;
  const certificatesListData = categorizeCertificates(uncategorizedCertificatesList);

  const Page = compose<Props, PropsFromDataFetcher>(
    Retracked.createTrackedContainer(() => ({
      namespace: {
        app: 'program_hub',
        page: 'product_variant_hub',
      },
    })),
    withUserAgent()
  )(ProductVariantHubPage);

  return (
    <Page
      pageContent={pageContent}
      certificatesList={certificatesListData?.certificatesList}
      categorizedCertificates={certificatesListData?.categorizedCertificates}
    />
  );
};

export default CertificatesCategoryHubPageDataFetcher;
